import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, trackStripeCta } from "@/lib/api";
import { openBillingPortal } from "@/lib/billing";
import { PLAN_LABELS } from "@/lib/mode";
import { AlertBell } from "@/components/AlertBell";
import { HeaderTip } from "@/components/HeaderTip";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SignOut, SquaresFour, Briefcase, User, Ticket, List, X, Camera, ArrowsClockwise, CircleNotch, Buildings, Question, Compass, CaretDown, CreditCard, Tag } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Below 1200px the secondary nav links drop their text labels; tooltips carry the names instead.
const COMPACT_QUERY = "(max-width: 1199px)";
function useCompactNav() {
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(COMPACT_QUERY);
    const onChange = (e) => setCompact(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return compact;
}

export function Header() {
  const { user, logout, updateUser } = useAuth();
  const compactNav = useCompactNav();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackNew, setFeedbackNew] = useState(0);
  const [resetting, setResetting] = useState(false);

  const handleResetDemo = async () => {
    if (resetting) return;
    setMenuOpen(false);
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-practice");
      toast.success("Demo reset — fresh Big Sky Wellness is ready");
      if (data?.business_id) navigate(`/business/${data.business_id}`);
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset demo data.");
    } finally {
      setResetting(false);
    }
  };

  const restartTour = () => {
    setMenuOpen(false);
    localStorage.removeItem("bk_tour_done_v1");
    navigate("/dashboard?tour=1");
  };

  useEffect(() => {
    if (user?.role !== "admin") { setFeedbackNew(0); return; }
    let cancelled = false;
    api.get("/admin/feedback").then((r) => {
      if (cancelled) return;
      const items = r.data || [];
      if (location.pathname === "/admin") {
        localStorage.setItem("bk_feedback_seen", items[0]?.created_at || new Date().toISOString());
        setFeedbackNew(0);
      } else {
        const seen = localStorage.getItem("bk_feedback_seen") || "";
        setFeedbackNew(items.filter((f) => (f.created_at || "") > seen).length);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  const planState = user?.plan;
  const trialLeft = user?.trial_days_left ?? 0;
  const planNeedsAttn = planState === "trial" || planState === "expired";
  const isLandingGuest = !user && location.pathname === "/";
  const isPricingGuest = !user && location.pathname === "/pricing";
  const useStripeSignup = isLandingGuest || isPricingGuest;
  const planText = planState === "trial" ? `Trial · ${trialLeft}d left`
    : planState === "expired" ? "Trial ended · Upgrade"
    : (PLAN_LABELS[planState] || "Plan");
  const doLogout = () => {
    logout();
    navigate("/");
  };

  const handleManageBilling = async () => {
    setMenuOpen(false);
    try {
      await openBillingPortal({ returnPath: location.pathname === "/settings" ? "/settings" : location.pathname + location.search });
    } catch (err) {
      toast.error(err.message || "Couldn't open billing.");
    }
  };

  const handleScan = async () => {
    setMenuOpen(false);
    const m = location.pathname.match(/\/business\/([^/?#]+)/);
    let id = m?.[1];
    if (!id) {
      try {
        const { data } = await api.get("/businesses");
        if (!data || data.length === 0) {
          toast.error("Create a business first, then you can scan receipts.");
          navigate("/dashboard");
          return;
        }
        id = data[0].id;
      } catch {
        toast.error("Couldn't open the scanner. Please try again.");
        return;
      }
    }
    navigate(`/business/${id}?tab=receipts`);
    setTimeout(() => window.dispatchEvent(new CustomEvent("bk-open-scan")), 60);
  };

  const toggleMode = async (mode) => {
    if (!user || user.mode === mode) return;
    updateUser({ mode });
    try {
      await api.put("/auth/mode", { mode });
    } catch {
      toast.error("Couldn't switch mode");
    }
  };

  const ModeToggle = ({ testidPrefix = "" }) => (
    <div className="flex items-center rounded-full p-0.5 border shrink-0" style={{ borderColor: "rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)" }} data-testid={`${testidPrefix}mode-toggle`}>
      {[
        { key: "bookkeeper", label: "Bookkeeper", icon: Briefcase },
        { key: "owner", label: "Owner", icon: User },
      ].map((m) => {
        const active = (user.mode || "bookkeeper") === m.key;
        return (
          <button
            key={m.key}
            onClick={() => toggleMode(m.key)}
            data-testid={`${testidPrefix}mode-${m.key}`}
            className="flex items-center gap-1.5 px-2.5 lg:px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap"
            style={active ? { background: "var(--bk-gold)", color: "var(--bk-accent)" } : { color: "rgba(255,255,255,0.88)" }}
          >
            <m.icon size={14} weight="duotone" /> {m.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <header
      className="sticky top-0 z-40 border-b"
      style={isLandingGuest
        ? { background: "#F0E6D3", borderColor: "rgba(26,39,68,0.08)", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
        : { background: "var(--bk-accent)", borderColor: "rgba(255,255,255,0.08)", boxShadow: "0 10px 24px rgba(26,39,68,0.12)" }}
    >
      <TooltipProvider delayDuration={150} skipDelayDuration={400}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-3">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5 shrink-0 overflow-visible" data-testid="brand-logo">
          <span className="w-auto h-[56px] flex items-center justify-center overflow-visible shrink-0" data-testid="brand-logo-mark-wrap">
            <img src="/ironroot-logo.png" alt="IronRoot" className="w-auto h-[56px] object-contain shrink-0 overflow-visible" style={{ display: "block" }} />
          </span>
          <span className="block font-display text-base sm:text-xl lg:text-[1.55rem] font-bold tracking-tight whitespace-nowrap leading-none" style={{ color: isLandingGuest ? "var(--bk-accent)" : "#FFFFFF" }}>
            IronRoot
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-1.5 flex-1 justify-end min-w-0 flex-nowrap">
          {user ? (
            <>
              <ModeToggle />
              <button onClick={handleScan} data-testid="nav-scan-btn" className="bk-btn-energy flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold shrink-0 whitespace-nowrap">
                <Camera size={16} weight="fill" /> Scan
              </button>
              <HeaderTip label="Business" enabled={compactNav}>
                <Link to="/dashboard" data-testid="nav-business" aria-label="Business" className="flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm font-medium hover:bg-white/10 transition-colors shrink-0 whitespace-nowrap" style={{ color: "#FFFFFF" }}>
                  <Briefcase size={16} weight="duotone" /> <span className="hidden min-[1200px]:inline">Business</span>
                </Link>
              </HeaderTip>
              <HeaderTip label={planNeedsAttn ? `Plan & pricing — ${planText}` : "Plan & pricing"} enabled={compactNav || !planNeedsAttn}>
                <Link
                  to="/pricing"
                  data-testid="nav-pricing"
                  aria-label={planNeedsAttn ? planText : "Plan & pricing"}
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm font-medium border transition-colors hover:bg-white/10 shrink-0 whitespace-nowrap"
                  style={{ borderColor: planNeedsAttn ? "var(--bk-gold)" : "rgba(255,255,255,0.14)", color: planNeedsAttn ? "var(--bk-gold)" : "#FFFFFF" }}
                >
                  <Tag size={16} weight="duotone" /> {planNeedsAttn ? <span>{planText}</span> : <span className="hidden min-[1200px]:inline">Plan &amp; pricing</span>}
                </Link>
              </HeaderTip>
              <HeaderTip label="Dashboard" enabled={compactNav}>
                <Link to="/dashboard" data-testid="nav-dashboard" aria-label="Dashboard" className="flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm font-medium hover:bg-white/10 transition-colors shrink-0 whitespace-nowrap" style={{ color: "#FFFFFF" }}>
                  <SquaresFour size={18} weight="duotone" /> <span className="hidden min-[1200px]:inline">Dashboard</span>
                </Link>
              </HeaderTip>
              <HeaderTip label="Formation" enabled={compactNav}>
                <Link to="/formation" data-testid="nav-formation" aria-label="Formation" className="flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm font-medium hover:bg-white/10 transition-colors shrink-0 whitespace-nowrap" style={{ color: "#FFFFFF" }}>
                  <Buildings size={18} weight="duotone" /> <span className="hidden min-[1200px]:inline">Formation</span>
                </Link>
              </HeaderTip>
              <DropdownMenu>
                <HeaderTip label="Help">
                  <DropdownMenuTrigger asChild>
                    <button data-testid="help-menu-btn" aria-label="Help" className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors" style={{ color: "#FFFFFF" }}>
                      <Question size={19} weight="duotone" />
                    </button>
                  </DropdownMenuTrigger>
                </HeaderTip>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem data-testid="restart-tour-item" onClick={restartTour} className="cursor-pointer gap-2">
                    <Compass size={16} weight="duotone" /> Restart tour
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <AlertBell />
              <DropdownMenu>
                <HeaderTip label="Account">
                  <DropdownMenuTrigger asChild>
                    <button data-testid="user-menu-trigger" aria-label="Account" className="flex items-center gap-1.5 pl-1 pr-2 py-1.5 rounded-full border transition-colors hover:bg-white/10 shrink-0" style={{ borderColor: "rgba(255,255,255,0.14)", color: "#FFFFFF", background: "rgba(255,255,255,0.06)" }}>
                      <span data-testid="user-avatar-chip" className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.16)", color: "#FFFFFF" }}>
                        <User size={18} weight="duotone" />
                      </span>
                      <CaretDown size={14} weight="bold" style={{ color: "#FFFFFF" }} />
                    </button>
                  </DropdownMenuTrigger>
                </HeaderTip>
                <DropdownMenuContent align="end" className="bg-white min-w-[220px]">
                  <DropdownMenuLabel data-testid="user-menu-label">
                    <div className="flex flex-col">
                      <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{user?.name || "IronRoot user"}</span>
                      <span className="text-xs font-normal" style={{ color: planNeedsAttn ? "var(--bk-accent)" : "var(--bk-text-2)" }}>{planText}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="user-menu-manage-billing" onClick={handleManageBilling} className="cursor-pointer gap-2">
                    <CreditCard size={16} weight="duotone" /> Manage Billing →
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="user-menu-settings" onClick={() => navigate("/settings")} className="cursor-pointer gap-2">
                    <User size={16} weight="duotone" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="user-menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer gap-2" style={{ color: "var(--bk-accent)" }}>
                      <span className="relative inline-flex items-center gap-2">
                        <Ticket size={16} weight="duotone" /> Admin
                        {feedbackNew > 0 && (
                          <span data-testid="admin-feedback-badge" className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ background: "#B23B1F" }}>{feedbackNew}</span>
                        )}
                      </span>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="user-menu-reset-demo" onClick={handleResetDemo} disabled={resetting} className="cursor-pointer gap-2">
                      {resetting ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} weight="duotone" />} Reset demo
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="user-menu-logout" onClick={doLogout} className="cursor-pointer gap-2">
                    <SignOut size={16} weight="duotone" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login"
                className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
                style={isLandingGuest ? { color: "#1A2744" } : { color: "#FFFFFF" }}
              >
                Log in
              </Link>
              {useStripeSignup ? (
                <Link
                  to="/register"
                  onClick={() => trackStripeCta(isLandingGuest ? "Starter" : "Professional", "navbar", isLandingGuest ? "Get started navbar" : "Get started pricing navbar")}
                  data-testid="nav-register"
                  className="px-5 py-2 rounded-full text-sm font-medium"
                  style={isLandingGuest ? { background: "#1A2744", color: "#F0E6D3", boxShadow: "0 8px 20px rgba(26,39,68,0.16)" } : { background: "#6B46C1", color: "#FFFFFF", boxShadow: "0 8px 20px rgba(107,70,193,0.22)" }}
                >
                  Get started
                </Link>
              ) : (
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="px-5 py-2 rounded-full text-sm font-medium"
                >
                  Get started
                </Link>
              )}
            </>
          )}
        </nav>

        {/* Mobile menu button */}
        <div className="flex lg:hidden items-center gap-2">
          {!user && (
            useStripeSignup ? (
              <Link
                to="/register"
                onClick={() => trackStripeCta("Professional", "navbar", isLandingGuest ? "Get started mobile navbar" : "Get started mobile pricing navbar")}
                data-testid="nav-register-m"
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={isLandingGuest ? { background: "#1A2744", color: "#F0E6D3", boxShadow: "0 8px 20px rgba(26,39,68,0.16)" } : { background: "#6B46C1", color: "#FFFFFF", boxShadow: "0 8px 20px rgba(107,70,193,0.22)" }}
              >
                Get started
              </Link>
            ) : (
              <Link
                to="/register"
                data-testid="nav-register-m"
                className="px-4 py-2 rounded-full text-sm font-medium"
              >
                Get started
              </Link>
            )
          )}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            className="p-2 rounded-full border"
            style={isLandingGuest
              ? { borderColor: "rgba(26,39,68,0.12)", color: "var(--bk-accent)", background: "#F6EFE2" }
              : { borderColor: "rgba(255,255,255,0.16)", color: "#FFFFFF", background: "rgba(255,255,255,0.06)" }}
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="lg:hidden border-t px-6 py-4 space-y-3" style={isLandingGuest ? { borderColor: "rgba(26,39,68,0.08)", background: "#F6EFE2" } : { borderColor: "rgba(255,255,255,0.08)", background: "var(--bk-accent)" }} data-testid="mobile-menu">
          {user ? (
            <>
              <ModeToggle testidPrefix="m-" />
              <div className="flex items-center justify-between gap-3 py-1">
                <div className="flex items-center gap-3 min-w-0">
                  <span data-testid="m-user-avatar-chip" className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.16)", color: "#FFFFFF" }}>
                    <User size={18} weight="duotone" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate text-white">{user?.name || "IronRoot user"}</p>
                    <p className="text-xs truncate" style={{ color: planNeedsAttn ? "var(--bk-gold)" : "rgba(255,255,255,0.72)" }}>{planText}</p>
                  </div>
                </div>
                <AlertBell />
              </div>
              <button onClick={handleScan} data-testid="m-menu-scan-btn" className="flex items-center gap-2 py-2 text-sm font-semibold w-full" style={{ color: "var(--bk-gold)" }}>
                <Camera size={18} weight="fill" /> Scan a receipt
              </button>
              <Link to="/dashboard" onClick={() => setMenuOpen(false)} data-testid="m-nav-business" className="flex items-center gap-2 py-2 text-sm font-medium text-white">
                <Briefcase size={18} weight="duotone" /> Business
              </Link>
              <Link to="/pricing" onClick={() => setMenuOpen(false)} data-testid="m-nav-pricing" className="flex items-center gap-2 py-2 text-sm font-medium" style={{ color: planNeedsAttn ? "var(--bk-gold)" : "#FFFFFF" }}>
                <Tag size={18} weight="duotone" /> {planNeedsAttn ? planText : "Plan & pricing"}
              </Link>
              <Link to="/dashboard" onClick={() => setMenuOpen(false)} data-testid="m-nav-dashboard" className="flex items-center gap-2 py-2 text-sm font-medium text-white">
                <SquaresFour size={18} weight="duotone" /> Dashboard
              </Link>
              <Link to="/settings" onClick={() => setMenuOpen(false)} data-testid="m-nav-settings" className="flex items-center gap-2 py-2 text-sm font-medium text-white">
                <User size={18} weight="duotone" /> Settings
              </Link>
              <button onClick={handleManageBilling} data-testid="m-manage-billing-btn" className="flex items-center gap-2 py-2 text-sm font-medium w-full text-white">
                <CreditCard size={18} weight="duotone" /> Manage Billing →
              </button>
              <Link to="/formation" onClick={() => setMenuOpen(false)} data-testid="m-nav-formation" className="flex items-center gap-2 py-2 text-sm font-medium text-white">
                <Buildings size={18} weight="duotone" /> Formation
              </Link>
              <button onClick={restartTour} data-testid="m-restart-tour" className="flex items-center gap-2 py-2 text-sm font-medium w-full text-white">
                <Compass size={18} weight="duotone" /> Restart tour
              </button>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setMenuOpen(false)} data-testid="m-nav-admin" className="flex items-center gap-2 py-2 text-sm font-semibold" style={{ color: "var(--bk-gold)" }}>
                  <Ticket size={18} weight="duotone" /> Admin
                  {feedbackNew > 0 && (
                    <span data-testid="m-admin-feedback-badge" className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ background: "#B23B1F" }}>{feedbackNew}</span>
                  )}
                </Link>
              )}
              {user.role === "admin" && (
                <button onClick={handleResetDemo} disabled={resetting} data-testid="m-reset-demo-btn" className="flex items-center gap-2 py-2 text-sm font-medium w-full text-white">
                  {resetting ? <CircleNotch size={18} className="animate-spin" /> : <ArrowsClockwise size={18} weight="duotone" />} Reset demo
                </button>
              )}
              <button onClick={() => { setMenuOpen(false); doLogout(); }} data-testid="m-logout-btn" className="flex items-center gap-2 py-2 text-sm font-medium w-full text-white">
                <SignOut size={18} weight="duotone" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMenuOpen(false)} data-testid="m-nav-login" className="flex items-center gap-2 py-2 text-sm font-medium" style={{ color: isLandingGuest ? "#1A2744" : "#FFFFFF" }}>Log in</Link>
              <Link to="/pricing" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium" style={{ color: isLandingGuest ? "#1A2744" : "#FFFFFF" }}>Pricing</Link>
            </>
          )}
        </div>
      )}
      </TooltipProvider>
    </header>
  );
}
